import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api, formatCOP } from '../services/api';
import useInvalidate from '../hooks/useInvalidate';
import AdminPedidoDetail from '../components/AdminPedidoDetail';
import { pedidoClienteLabel } from '../utils/textEncoding';

const ESTADO = {
  pendiente: 'Pendiente',
  confirmado: 'Confirmado',
  en_preparacion: 'En prep.',
  entregado: 'Entregado',
  cancelado: 'Cancelado',
};

const FILTROS = [
  { value: '', label: 'Todos' },
  { value: 'pendiente', label: 'Pendientes' },
  { value: 'confirmado', label: 'Confirmados' },
  { value: 'en_preparacion', label: 'En preparación' },
  { value: 'entregado', label: 'Entregados' },
  { value: 'cancelado', label: 'Cancelados' },
];

function localISODate(d = new Date()) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Primer día del mes actual (hora local — evita July por UTC). */
function defaultDesde() {
  const d = new Date();
  return localISODate(new Date(d.getFullYear(), d.getMonth(), 1));
}

function defaultHasta() {
  return localISODate(new Date());
}

function fmtFecha(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('es-CO', { dateStyle: 'short', timeStyle: 'short' });
}

export default function PedidosPage({ pin }) {
  const [desde, setDesde] = useState(defaultDesde);
  const [hasta, setHasta] = useState(defaultHasta);
  const [estado, setEstado] = useState('');
  const [vendedorId, setVendedorId] = useState('');
  const [preparadoPorId, setPreparadoPorId] = useState('');
  const [vendedores, setVendedores] = useState([]);
  const [bodegaUsers, setBodegaUsers] = useState([]);
  const [porPreparado, setPorPreparado] = useState(false);
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [pedidos, setPedidos] = useState([]);
  const [resumen, setResumen] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const loadSeq = React.useRef(0);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 500);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    let cancelled = false;
    api
      .getVendedores()
      .then((r) => {
        if (!cancelled) setVendedores(r.data || []);
      })
      .catch(() => {
        if (!cancelled) setVendedores([]);
      });
    api
      .getUsuarios()
      .then((r) => {
        if (cancelled) return;
        const list = (r.data || []).filter(
          (u) => u.activo !== 0 && (u.rol === 'bodega' || u.rol === 'admin' || u.rol === 'oficina')
        );
        setBodegaUsers(list);
      })
      .catch(() => {
        if (!cancelled) setBodegaUsers([]);
      });
    return () => {
      cancelled = true;
    };
  }, [pin]);

  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    setLoading(true);
    setError('');
    try {
      const res = await api.getAdminPedidos({
          desde,
          hasta,
          estado: estado || undefined,
          q: debouncedQ || undefined,
          vendedor_id: vendedorId || undefined,
          preparado_por_id: preparadoPorId || undefined,
          resumen: true,
          limit: 200,
          por: porPreparado ? 'preparado' : undefined,
        });
      if (seq !== loadSeq.current) return;
      setPedidos(res.data || []);
      setResumen(res.resumen || null);
    } catch (err) {
      if (seq !== loadSeq.current) return;
      setPedidos([]);
      setResumen(null);
      setError(err.message || 'No se pudieron cargar los pedidos');
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [pin, desde, hasta, estado, debouncedQ, porPreparado, vendedorId, preparadoPorId]);

  useEffect(() => {
    load();
  }, [load]);

  useInvalidate(['order'], load);

  const prepOptions = useMemo(() => {
    const map = new Map();
    for (const u of bodegaUsers) {
      map.set(String(u.id), u.nombre);
    }
    for (const p of pedidos) {
      if (p.preparado_por_id && !map.has(String(p.preparado_por_id))) {
        map.set(String(p.preparado_por_id), p.preparado_por_nombre || `Operario #${p.preparado_por_id}`);
      }
    }
    if (resumen?.por_preparado) {
      for (const r of resumen.por_preparado) {
        if (r.preparado_por_id && !map.has(String(r.preparado_por_id))) {
          map.set(String(r.preparado_por_id), r.nombre);
        }
      }
    }
    return [...map.entries()]
      .map(([id, nombre]) => ({ id, nombre }))
      .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
  }, [bodegaUsers, pedidos, resumen]);

  return (
    <div className="panel pedidos-admin">
      <header className="panel-head">
        <div>
          <h2>Historial de pedidos</h2>
          <span>Pedidos por vendedor, bodega, facturación y detalle completo</span>
        </div>
      </header>

      <div className="pedidos-admin-filters">
        <label className="date-field">
          <span>Desde</span>
          <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} />
        </label>
        <label className="date-field">
          <span>Hasta</span>
          <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} />
        </label>
        <label className="date-field">
          <span>Vendedor</span>
          <select value={vendedorId} onChange={(e) => setVendedorId(e.target.value)}>
            <option value="">Todos</option>
            {vendedores.map((v) => (
              <option key={v.id} value={v.id}>
                {v.nombre}
              </option>
            ))}
          </select>
        </label>
        <label className="date-field">
          <span>Separado por</span>
          <select value={preparadoPorId} onChange={(e) => setPreparadoPorId(e.target.value)}>
            <option value="">Todos</option>
            {prepOptions.map((u) => (
              <option key={u.id} value={u.id}>
                {u.nombre}
              </option>
            ))}
          </select>
        </label>
        <input
          type="search"
          placeholder="Buscar número, cliente, factura, remisión..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && load()}
        />
        <button type="button" onClick={load}>Filtrar</button>
      </div>

      <label className="por-preparado-toggle">
        <input
          type="checkbox"
          checked={porPreparado}
          onChange={(e) => setPorPreparado(e.target.checked)}
        />
        <span>Filtrar por fecha de separación (bodega)</span>
      </label>

      <div className="pedidos-admin-chips">
        {FILTROS.map((f) => (
          <button
            key={f.value || 'all'}
            type="button"
            className={`chip-btn ${estado === f.value ? 'active' : ''}`}
            onClick={() => setEstado(f.value)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {resumen ? (
        <>
          <div className="stat-grid pedidos-admin-stats">
            <div className="stat-card stat-purple">
              <span>Total en rango</span>
              <strong>{resumen.total}</strong>
            </div>
            <div className="stat-card stat-green">
              <span>Entregados</span>
              <strong>{resumen.entregado}</strong>
            </div>
            <div className="stat-card stat-orange">
              <span>Pendientes</span>
              <strong>{resumen.pendiente}</strong>
            </div>
            <div className="stat-card stat-pink">
              <span>Ventas</span>
              <strong>{formatCOP(resumen.ventas)}</strong>
            </div>
            <div className="stat-card">
              <span>Confirmados</span>
              <strong>{resumen.confirmado}</strong>
            </div>
            <div className="stat-card">
              <span>En preparación</span>
              <strong>{resumen.en_preparacion}</strong>
            </div>
          </div>

          <div className="stats-breakdown">
            {resumen.por_vendedor?.length ? (
              <div className="dash-panel">
                <div className="dash-panel-head">
                  <h2>Pedidos por vendedor</h2>
                </div>
                <ul className="campo-list">
                  {resumen.por_vendedor.map((r) => (
                    <li key={r.vendedor_id ?? r.nombre}>
                      <div>
                        <strong>{r.nombre}</strong>
                        <span>{formatCOP(r.ventas)} en ventas</span>
                      </div>
                      <em>{r.n} pedido{r.n !== 1 ? 's' : ''}</em>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            {resumen.por_preparado?.length ? (
              <div className="dash-panel">
                <div className="dash-panel-head">
                  <h2>Separados por bodega</h2>
                </div>
                <ul className="campo-list">
                  {resumen.por_preparado.map((r) => (
                    <li key={r.preparado_por_id ?? r.nombre}>
                      <div>
                        <strong>{r.nombre}</strong>
                        <span>Operario bodega</span>
                      </div>
                      <em>{r.n} pedido{r.n !== 1 ? 's' : ''}</em>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        </>
      ) : null}

      {error ? <p className="error banner">{error}</p> : null}
      {loading ? <p className="muted">Cargando pedidos...</p> : null}

      {!loading && !pedidos.length ? (
        <p className="muted">No hay pedidos en este rango de fechas.</p>
      ) : null}

      {!loading && pedidos.length > 0 ? (
        <div className="table-wrap">
          <table className="visitas-table pedidos-admin-table responsive-cards">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Pedido</th>
                <th>Cliente</th>
                <th>Vendedor</th>
                <th>Estado</th>
                <th>Separado por</th>
                <th>Documento</th>
                <th>Nº / código</th>
                <th>Quién factura</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {pedidos.map((p) => (
                <tr key={p.id} className="clickable-row" onClick={() => setSelectedId(p.id)}>
                  <td data-label="Fecha">{fmtFecha(porPreparado && (p.revisado_at || p.preparado_at) ? (p.revisado_at || p.preparado_at) : p.created_at)}</td>
                  <td className="mono" data-label="Pedido">{p.numero}</td>
                  <td data-label="Cliente">
                    {pedidoClienteLabel(p)}
                    {p.es_sede || p.padre_codigo ? (
                      <span className="cliente-sede-pill"> Sucursal</span>
                    ) : null}
                  </td>
                  <td data-label="Vendedor">
                    {p.origen === 'web' ? (
                      <span className="origen-pill origen-web">Tienda web</span>
                    ) : (
                      p.vendedor || '—'
                    )}
                  </td>
                  <td data-label="Estado">
                    <span className={`estado-pill estado-${p.estado}`}>{ESTADO[p.estado] || p.estado}</span>
                  </td>
                  <td data-label="Separado por">{p.preparado_por_nombre || '—'}</td>
                  <td data-label="Documento">
                    {String(p.tipo_documento || '').toLowerCase() === 'iva'
                      ? 'Factura'
                      : String(p.tipo_documento || '').toLowerCase() === 'remision'
                        ? 'Remisión'
                        : '—'}
                  </td>
                  <td className="mono" data-label="Nº / código">{p.documento_numero || '—'}</td>
                  <td data-label="Quién factura">{p.facturado_por_nombre || '—'}</td>
                  <td data-label="Total">{formatCOP(p.subtotal)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {selectedId ? (
        <AdminPedidoDetail id={selectedId} pin={pin} onClose={() => setSelectedId(null)} />
      ) : null}
    </div>
  );
}
